// src/routes/controlChanges.tsx
import { useState, useMemo, ReactElement } from "react";
import { useParams } from "react-router-dom";
import { useMusicStore } from "~/stores/useMusicStore";
import { useAuthStore } from "~/stores/useAuthStore";
import AuthModal from "~/components/AuthModal";
import RangeSlider from "~/components/ui/RangeSlider";
import Switch from "~/components/ui/Switch";
import SectionHeader from "~/components/ui/SectionHeader";
import ScrambleToggle from "~/components/ui/ScrambleToggle";
import type { MidiDevice, MidiCC } from "~/types/midi";

export default function ControlChanges(): ReactElement {
  const { deviceIndex: deviceIndexParam } = useParams<{ deviceIndex: string }>();
  const deviceIndex = parseInt(deviceIndexParam || "0", 10);

  const devices = useMusicStore((state) => state.devices);
  const setDevices = useMusicStore((state) => state.setDevices);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const handleUnauthenticatedClick = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
    }
  };

  const selectedDevice = useMemo<MidiDevice | undefined>(() => devices[deviceIndex], [devices, deviceIndex]);

  // Check if this is a MIDI hardware device (not a virtual synth)
  // Virtual devices have synthType, MIDI hardware devices have productId
  const isMidiDevice = useMemo<boolean>(() => {
    return !!selectedDevice?.productId;
  }, [selectedDevice]);

  // Determine if auth restriction should apply
  // Only MIDI hardware devices require authentication
  const requiresAuth = useMemo<boolean>(() => {
    return isMidiDevice && !isAuthenticated;
  }, [isMidiDevice, isAuthenticated]);

  // Filter CCs based on showAll toggle (only for hardware devices)
  const visibleCCs = useMemo<MidiCC[]>(() => {
    if (!selectedDevice) return [];
    // Virtual devices: only show CCs with show: true
    // Hardware devices: respect showAll toggle
    if (!isMidiDevice) {
      return selectedDevice.cc.filter((cc: MidiCC) => cc.show);
    }
    return showAll ? selectedDevice.cc : selectedDevice.cc.filter((cc: MidiCC) => cc.show);
  }, [selectedDevice, showAll, isMidiDevice]);

  // Handle CC slider change (value, min, max)
  const handleCCChange = (ccNumber: number, value: number, min: number, max: number) => {
    setDevices((devices) => {
      const device = devices[deviceIndex];
      if (device && device.cc) {
        const ccIndex = device.cc.findIndex((cc) => cc.number === ccNumber);
        if (ccIndex !== -1) {
          device.cc[ccIndex].value = value;
          device.cc[ccIndex].min = min;
          device.cc[ccIndex].max = max;
        }
      }
    });
  };

  // Handle CC label change
  const handleLabelChange = (ccNumber: number, newLabel: string) => {
    setDevices((devices) => {
      const device = devices[deviceIndex];
      if (device && device.cc) {
        const ccIndex = device.cc.findIndex((cc) => cc.number === ccNumber);
        if (ccIndex !== -1) {
          device.cc[ccIndex].label = newLabel;
        }
      }
    });
  };

  // Handle scramble toggle
  const handleScrambleToggle = (ccNumber: number) => {
    setDevices((devices) => {
      const device = devices[deviceIndex];
      if (device && device.cc) {
        const ccIndex = device.cc.findIndex((cc) => cc.number === ccNumber);
        if (ccIndex !== -1) {
          device.cc[ccIndex].scramble = !device.cc[ccIndex].scramble;
        }
      }
    });
  };

  return (
    <div className="flex-1 h-full flex flex-col">
      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />

      <div className="flex-1 flex flex-col p-4 overflow-auto" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        <div className="relative">
          {/* Click Interception Overlay - only for MIDI hardware devices when not authenticated */}
          {requiresAuth && (
            <div
              className="absolute inset-0 z-10 cursor-pointer"
              onClick={handleUnauthenticatedClick}
              title="Sign in to adjust parameters"
            />
          )}

          {/* Show All Toggle - Only for hardware devices */}
          {isMidiDevice ? (
            <div className="flex items-center justify-between mb-6 pb-2 border-b border-gray-800">
              <span className="text-sm text-gray-400">
                {visibleCCs.length} / {selectedDevice?.cc.length || 0} CC{selectedDevice?.cc.length !== 1 ? 's' : ''}
              </span>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-gray-300">Show all</span>
                <Switch
                  checked={showAll}
                  onCheckedChange={setShowAll}
                  aria-label={showAll ? "Hide unsupported CCs" : "Show all 128 CCs"}
                />
              </label>
            </div>
          ) : (
            <div className="mb-6 pb-2 border-b border-gray-800">
              <span className="text-sm text-gray-400">
                {visibleCCs.length} CC{visibleCCs.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* CC List with Range Sliders */}
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-x-8 lg:[&>*:nth-child(-n+2)]:mt-0 ${requiresAuth ? 'pointer-events-none opacity-50' : ''}`}>
            {visibleCCs.map((cc: MidiCC) => {
              const currentValue = cc.value ?? cc.min;

              return (
                <div key={cc.number} className="min-h-0 mt-12 first:mt-0">
                  {/* Section Header with editable title and scramble toggle */}
                  <SectionHeader
                    title={cc.label || ""}
                    prefix={cc.number.toString()}
                    variant="control"
                    onTitleChange={(newLabel) => handleLabelChange(cc.number, newLabel)}
                  >
                    <ScrambleToggle
                      enabled={cc.scramble}
                      onToggle={() => handleScrambleToggle(cc.number)}
                    />
                  </SectionHeader>

                  {/* Range Slider */}
                  <div className="w-full space-y-4">
                    <RangeSlider
                      value={currentValue}
                      valueMin={cc.min}
                      valueMax={cc.max}
                      min={0}
                      max={127}
                      step={1}
                      onChange={(value, min, max) => handleCCChange(cc.number, value, min, max)}
                      disabled={requiresAuth}
                      scrambleEnabled={cc.scramble}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
